'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { 
  ShoppingCart, 
  Leaf, 
  Building2, 
  FileText, 
  BadgeDollarSign, 
  Stethoscope, 
  Scale, 
  ShieldCheck,
  Car,
  Zap,
  Globe,
  Utensils,
  TrendingUp
} from "lucide-react";

// Enhanced gradient palette with unique gradients for each industry
const industries = [
  {
    name: "AutoTech",
    description: "Automotive Technology",
    icon: Car,
    gradient: "from-amber-500 via-orange-500 to-red-500",
    glowColor: "shadow-orange-500/25",
    solutions: ["Vehicle Compliance Scanning", "Predictive Maintenance", "Autonomous Vehicle Systems"],
    href: "/industries/autotech"
  },
  {
    name: "EnergyTech",
    description: "Energy Technology",
    icon: Zap,
    gradient: "from-yellow-400 via-orange-500 to-red-500",
    glowColor: "shadow-yellow-500/25",
    solutions: ["Smart Grid Management", "Renewable Energy Forecasting", "Energy Consumption Analytics"],
    href: "/industries/energytech"
  },
  {
    name: "GeoTech",
    description: "Geospatial Technology",
    icon: Globe,
    gradient: "from-blue-500 via-cyan-500 to-teal-500",
    glowColor: "shadow-blue-500/25",
    solutions: ["Satellite Imagery Analysis", "Location Intelligence", "Precision Agriculture Systems"],
    href: "/industries/geotech"
  },
  {
    name: "FoodTech",
    description: "Food Technology",
    icon: Utensils,
    gradient: "from-green-500 via-emerald-500 to-teal-600",
    glowColor: "shadow-green-500/25",
    solutions: ["Supply Chain Traceability", "Nutritional Analysis", "Quality Control Automation"],
    href: "/industries/foodtech"
  },
  {
    name: "MarTech",
    description: "Marketing Technology",
    icon: ShoppingCart,
    gradient: "from-rose-500 via-pink-500 to-purple-500",
    glowColor: "shadow-rose-500/25",
    solutions: ["Customer Segmentation AI", "Predictive Analytics", "Content Generation"],
    href: "/industries/martech"
  },
  {
    name: "AgriTech",
    description: "Agricultural Technology",
    icon: Leaf,
    gradient: "from-green-500 via-emerald-500 to-teal-600",
    glowColor: "shadow-green-500/25",
    solutions: ["Crop Monitoring", "Yield Prediction", "Smart Irrigation Systems"],
    href: "/industries/agritech"
  },
  {
    name: "GovTech",
    description: "Government Technology",
    icon: Building2,
    gradient: "from-indigo-500 via-purple-500 to-pink-500",
    glowColor: "shadow-indigo-500/25",
    solutions: ["Public Service Automation", "Civic Engagement Platforms", "Smart City Solutions"],
    href: "/industries/govtech"
  },
  {
    name: "RegTech",
    description: "Regulatory Technology",
    icon: FileText,
    gradient: "from-amber-500 via-orange-500 to-red-500",
    glowColor: "shadow-orange-500/25",
    solutions: ["Compliance Monitoring", "Risk Assessment", "Regulatory Reporting"],
    href: "/industries/regtech"
  },
  {
    name: "FinTech",
    description: "Financial Technology",
    icon: BadgeDollarSign,
    gradient: "from-violet-600 via-indigo-500 to-blue-500",
    glowColor: "shadow-violet-500/25",
    solutions: ["Fraud Detection", "Algorithmic Trading", "Credit Scoring"],
    href: "/industries/fintech"
  },
  {
    name: "HealthTech",
    description: "Healthcare Technology",
    icon: Stethoscope,
    gradient: "from-cyan-500 via-blue-600 to-purple-600",
    glowColor: "shadow-cyan-500/25",
    solutions: ["Medical Imaging Analysis", "Patient Monitoring", "Drug Discovery"],
    href: "/industries/healthtech"
  },
  {
    name: "LegalTech",
    description: "Legal Technology",
    icon: Scale,
    gradient: "from-indigo-500 via-purple-500 to-pink-500",
    glowColor: "shadow-indigo-500/25",
    solutions: ["Contract Analysis", "Legal Research Automation", "Case Prediction"],
    href: "/industries/legaltech"
  },
  {
    name: "InsureTech",
    description: "Insurance Technology",
    icon: ShieldCheck,
    gradient: "from-fuchsia-500 via-purple-600 to-indigo-500",
    glowColor: "shadow-fuchsia-500/25",
    solutions: ["Risk Assessment", "Claims Processing", "Fraud Detection"],
    href: "/industries/insuretech"
  }
];

export function IndustriesSection() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
        duration: 1.0
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 15,
        mass: 0.8,
        duration: 0.8
      } 
    }
  };

  // Create wave offsets for organic feel
  const getRowOffset = (index) => {
    return [0, 0.1, 0.05, 0.15, 0.08, 0.12, 0.03, 0.18, 0.09, 0.14, 0.06, 0.11][index % 12];
  };

  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16 max-w-4xl mx-auto"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
            {localizeSpelling("Specialized Industry Solutions", currentLocale)}
          </h2>
          <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
            {localizeSpelling(
              "Our AI technologies deliver transformative results across multiple industries, with specialized solutions tailored to your sector's unique challenges.",
              currentLocale
            )}
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {industries.map((industry, index) => {
            const IconComponent = industry.icon;
            
            return (
              <motion.div 
                key={industry.name}
                variants={itemVariants}
                custom={index}
                className="h-full"
                transition={{
                  delay: getRowOffset(index)
                }}
                whileHover={{ 
                  y: -8,
                  scale: 1.02,
                  transition: { 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 20,
                    duration: 0.4
                  }
                }}
              >
                <Link href={industry.href} className="block h-full group">
                  <div className={`rounded-2xl overflow-hidden h-full relative bg-gradient-to-br ${industry.gradient} p-[1px] shadow-lg hover:shadow-xl ${industry.glowColor} transition-all duration-500 group-hover:shadow-2xl`}>
                    
                    {/* Glass morphism background */}
                    <div className="absolute inset-[1px] bg-white/95 backdrop-blur-xl rounded-2xl">
                      {/* Subtle gradient overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
                      
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 group-hover:animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                    </div>
                    
                    {/* Content */}
                    <div className="relative z-10 p-7 flex flex-col h-full">
                      {/* Icon with gradient background */}
                      <div className={`relative mb-4 w-12 h-12 rounded-xl bg-gradient-to-br ${industry.gradient} p-3 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
                        <IconComponent 
                          className="w-full h-full text-white drop-shadow-sm" 
                          strokeWidth={1.5}
                        />
                        {/* Icon glow effect */}
                        <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${industry.gradient} blur-md opacity-50 group-hover:opacity-75 transition-opacity duration-300 -z-10`}></div>
                      </div>
                      
                      <div className="flex-1 space-y-3">
                        <div>
                          <h3 className="text-slate-900 text-xl font-bold mb-2 group-hover:text-slate-800 transition-colors duration-300">
                            {localizeSpelling(industry.name, currentLocale)}
                          </h3>
                          <p className="text-slate-600 text-sm font-medium leading-relaxed">
                            {localizeSpelling(industry.description, currentLocale)}
                          </p>
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="text-slate-800 text-xs font-semibold uppercase tracking-wider">
                            {localizeSpelling("AI Solutions:", currentLocale)}
                          </h4>
                          <ul className="space-y-2">
                            {industry.solutions.map((solution) => (
                              <li key={solution} className="text-slate-600 text-xs flex items-start group-hover:text-slate-700 transition-colors duration-300">
                                <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${industry.gradient} mr-2 mt-1.5 flex-shrink-0`}></div>
                                <span className="leading-relaxed">{localizeSpelling(solution, currentLocale)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* Subtle arrow indicator */}
                      <div className="flex justify-end mt-4">
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${industry.gradient} flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-110`}>
                          <TrendingUp className="w-3 h-3 text-white" strokeWidth={2} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
} 